import { gsap } from 'gsap';
import { transitionLog } from '../debug/transitionDebug.js';
import { getCardBackgroundUrl } from './cardBackgrounds.js';
import { Card } from './Card.js';
import { TransitionManager } from '../transitions/TransitionManager.js';
import { preloadChannelImage, runChannelWipe } from '../transitions/channelWipe.js';
import { CardVideos, getCardVisualSource } from './cardVideos.js';
import { WorldManager } from '../worlds/WorldManager.js';
import { wantsSurfGame } from '../worlds/tbd/surf/detect.js';
import { mountChannelBoot } from '../worlds/tbd/surf/bootScreen.js';

/**
 * Carousel
 * ─────────────────────────────────────────────────────────────
 * Horizontal drag carousel with GSAP-driven momentum.
 *
 *  - Pointer Events for one code path across mouse / touch / pen.
 *  - Document-level move/up listeners during drag (so the cursor can
 *    leave the carousel surface without breaking the gesture).
 *  - Manual velocity-decay momentum on release: GSAP tweens to a
 *    projected target with `power3.out`. Bounds clamped at the section
 *    edges. (Skipped GSAP InertiaPlugin — it's a Club GreenSock paid plugin.)
 *  - Click vs drag distinguished by max-distance threshold during the gesture.
 *  - Velocity-driven tilt on each card mid-drag, GSAP-eased back on release.
 *  - Lenis page scroll → per-card translateY lag (position-chase, not
 *    velocity-burst). Each card has its own ease factor so they trail
 *    behind the page scroll by different amounts and catch up when scroll
 *    stops. Carousel drag (horizontal) is independent.
 *  - IntersectionObserver triggers staggered entrance once on first reveal.
 *  - Single rAF tick composes each card's transform from its own state.
 *
 * Public API:
 *   destroy()
 */

const CLICK_THRESHOLD_PX = 8;          // max move distance still treated as a click
const VELOCITY_HISTORY = 5;            // rolling samples for release velocity
const MOMENTUM_AMPLIFY = 14;           // px-per-(px/ms-of-velocity)
const MOMENTUM_DURATION = 1.4;         // GSAP tween seconds
const TILT_PER_VELOCITY = 0.06;        // deg per (px/16ms) of drag velocity
const TILT_MAX_DEG = 6;

// Per-card scroll-lag chase. Each card lerps its private "follow value"
// toward the live scroll position with its own ease factor. The card's
// rendered Y offset = (target − follow), so slower-chasing cards drag
// further behind during scroll and catch up when motion stops.
//
// Symmetric pattern: outer cards chase fastest (least lag), middle card
// is the slowest (most lag) — gives a depth/parallax read across the row.
// Adjust per-card values to taste; values higher than 0.5 = barely visible,
// values below 0.10 = very heavy drag.
const SCROLL_LAG_LERPS = [0.30, 0.22, 0.16, 0.22, 0.30];
const SCROLL_LAG_MAX_PX = 80;          // hard cap on any individual card's lag offset

const WHEEL_HORIZONTAL_DOMINANCE = 1.2; // |dx| > |dy| * this → handle as horizontal

// Full MPA sub-sites; everything else opens in the home overlay.
const EXTERNAL_WORLDS = ['travel'];

const STACK_MQ = '(max-width: 720px)';
const isStacked = () => typeof matchMedia === 'function' && matchMedia(STACK_MQ).matches;
const isCoarse = () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
// Stacked phones, and any coarse pointer: the per-card scroll-lag chase
// slides cards independently of the finger and reads as stutter.
const lagOff = () => isStacked() || isCoarse();

const isReducedMotion =
  typeof matchMedia === 'function' &&
  matchMedia('(prefers-reduced-motion: reduce)').matches;

export class Carousel {
  constructor(root = document.getElementById('carousel'), { onCardClick } = {}) {
    if (!root) return;
    this.root = root;
    this.section = root.closest('.carousel-section') || root.parentElement;
    // `travel` / `chotbat` → `expandFromCard` (same grow + pixel reveal as in-place,
    // then MPA `location.assign`). Other worlds: `expandCard` + `WorldManager.open`.
    this.onCardClick =
      onCardClick ||
      ((world, cardEl) => {
        if (!world) return;
        const returnRect = cardEl.getBoundingClientRect();
        if (EXTERNAL_WORLDS.includes(world)) {
          transitionLog('carousel:card', { world, kind: 'external', time: performance.now() });
          TransitionManager.expandFromCard(cardEl, `/${world}/`);
          return;
        }
        transitionLog('carousel:card', { world, kind: 'inplace', time: performance.now() });
        // Start the handheld chunk during the card grow, not after it.
        if (world === 'mild-concern' && wantsSurfGame()) {
          import('../worlds/tbd/tbd.js');
          import('../worlds/tbd/surf/index.js');
        }
        // Warm the card art now: the channel wipe uses the exact same image
        // the fullscreen cover lands on, so the handoff is pixel-identical.
        const src = getCardVisualSource(cardEl);
        if (!src.element) void preloadChannelImage(src.imageUrl);
        TransitionManager.expandCard(cardEl, returnRect, async (coverLayer) => {
          // Handheld game chunk is large. Cover the wait so the tap doesn't
          // sit on a black frame while tbd.js / surf download.
          const boot = world === 'mild-concern' && wantsSurfGame()
            ? mountChannelBoot(document.body)
            : null;
          try {
            // Mount the world under the cover (overlay is visible but hidden by it).
            await WorldManager.open(world, cardEl, returnRect);
          } finally {
            boot?.remove();
          }
          // Change the channel: the card's live clip crumbles away to the world DOM.
          await runChannelWipe({
            source: src.element,
            imageUrl: src.element ? undefined : src.imageUrl,
            coverLayer,
          });
        });
      });

    this.cardEls = Array.from(root.querySelectorAll('.card'));
    // Card 1 → /carousel/1.webp … card 5 → 5.webp (DOM order, not a shared asset).
    this.cardEls.forEach((el, i) => {
      const u = getCardBackgroundUrl(i);
      el.style.setProperty('--card-bg', `url("${u}")`);
    });
    this.cards = this.cardEls.map((el, i) => new Card(el, i));
    // Each card is a little CRT tuned to its own clip; dice re-tunes them all.
    this.cardVideos = new CardVideos(this.cardEls, this.section);
    this._onRandomize = () => this.cardVideos.retune();
    document.addEventListener('randomize', this._onRandomize);

    // Position state
    this.x = 0;            // current rendered translateX
    this.minX = 0;         // most-scrolled (negative)
    this.maxX = 0;         // un-scrolled

    // Drag state
    this._dragging = false;
    this._activePointer = null;
    this._dragStartPointerX = 0;
    this._dragStartPointerY = 0;
    this._dragStartX = 0;
    this._dragMaxDist = 0;
    this._lastPointerX = 0;
    this._lastPointerT = 0;
    this._velocityHistory = [];

    this._momentumTween = null;

    // Per-card scroll-lag chase state.
    // _scrollTarget tracks Lenis' current smoothed scroll position; each
    // card's _cardScrollFollow[i] eases toward it at its own lerp rate.
    this._scrollTarget = (typeof window !== 'undefined' && window.scrollY) || 0;
    this._cardScrollFollow = this.cards.map(() => this._scrollTarget);

    this._compute();
    this._bindCarouselEvents();
    this._bindResize();
    this._bindLenisParallax();
    this._observeEntrance();

    this._raf = requestAnimationFrame(this._tick);

    // Off-screen cards don't need a frame loop. Restart when they scroll in
    // so the entrance tween still paints.
    if (isStacked() && typeof IntersectionObserver !== 'undefined' && this.section) {
      this._sectionIo = new IntersectionObserver((entries) => {
        const on = entries.some((e) => e.isIntersecting);
        if (on && !this._raf) this._raf = requestAnimationFrame(this._tick);
        else if (!on && this._raf) {
          cancelAnimationFrame(this._raf);
          this._raf = 0;
        }
      }, { rootMargin: '120px 0px' });
      this._sectionIo.observe(this.section);
    }
  }

  // ── boot helpers ────────────────────────────────────────────

  _compute() {
    // Overflow = (track's right edge at x=0) − (section's right edge).
    // Measuring the track's own right edge — not the last card's — lets
    // .carousel's `padding-right` act as a real trailing gutter that the
    // user can scroll into. (If we used lastCard.right the clamp would
    // snap the final card flush to the viewport and the trailing pad
    // would become invisible dead space.)
    if (!this.cardEls.length) {
      this.minX = 0;
      this.maxX = 0;
      return;
    }
    const carouselRect = this.root.getBoundingClientRect();
    const sectionRect = this.section.getBoundingClientRect();

    // carouselRect is post-transform; subtract this.x to get its right
    // edge as it would sit if the track were un-scrolled.
    const trackRightAtZero = carouselRect.right - this.x;
    const overflow = trackRightAtZero - sectionRect.right;

    // We translate via translateX, negative = scrolled-right.
    this.maxX = 0;
    this.minX = overflow > 0 && !isStacked() ? -overflow : 0;
    if (isStacked()) this.x = 0;

    // If a momentum tween is mid-flight, re-clamp its target
    this._clampTarget();
  }

  _clampTarget() {
    if (this._momentumTween && this._momentumTween.isActive()) {
      const v = this.x;
      if (v < this.minX || v > this.maxX) {
        this._momentumTween.kill();
        gsap.to(this, {
          x: Math.max(this.minX, Math.min(this.maxX, v)),
          duration: 0.4,
          ease: 'power2.out',
        });
      }
    }
  }

  // ── events ──────────────────────────────────────────────────

  _bindCarouselEvents() {
    this.root.addEventListener('pointerdown', this._onPointerDown);
    this.root.addEventListener('click', this._onClick);
    // Wheel on the section (not just the carousel track) so flicking the
    // trackpad horizontally anywhere in the section scrolls the row.
    this.section.addEventListener('wheel', this._onWheel, { passive: false });
  }

  _bindResize() {
    this._onResize = () => {
      this._compute();
      if (!isStacked() && !this._raf) this._raf = requestAnimationFrame(this._tick);
    };
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  _bindLenisParallax() {
    if (lagOff()) return;
    if (!window.lenis?.on) {
      // No Lenis available — fall back to native scroll tracking so the
      // chase still works (just less smooth than Lenis-driven).
      this._onNativeScroll = () => { this._scrollTarget = window.scrollY || 0; };
      window.addEventListener('scroll', this._onNativeScroll, { passive: true });
      return;
    }
    this._lenisOff = window.lenis.on('scroll', ({ scroll = 0 } = {}) => {
      // scroll = current smoothed page-scroll position in px.
      // The per-card chase happens in _tick — we just publish the target here.
      this._scrollTarget = scroll;
    });
  }

  _observeEntrance() {
    if (isReducedMotion) {
      // Snap to final state immediately
      this.cards.forEach((c) => { c.enterY = 0; c.opacity = 1; });
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      this.cards.forEach((c) => c.enter());
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.cards.forEach((c) => c.enter());
          io.disconnect();
        });
      },
      { threshold: 0.2 }
    );
    io.observe(this.section);
    this._entranceObserver = io;
  }

  // ── pointer drag ────────────────────────────────────────────

  _onPointerDown = (e) => {
    if (isStacked()) return; // vertical stack on phones: the page scrolls, we don't drag
    // Primary button only (or any touch/pen)
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    this._momentumTween?.kill();

    this._dragging = true;
    this._activePointer = e.pointerId;
    this._dragStartPointerX = e.clientX;
    this._dragStartPointerY = e.clientY;
    this._dragStartX = this.x;
    this._dragMaxDist = 0;
    this._lastPointerX = e.clientX;
    this._lastPointerT = e.timeStamp;
    this._velocityHistory.length = 0;

    this.root.classList.add('is-dragging');

    // Document-level listeners — gesture survives leaving the carousel.
    document.addEventListener('pointermove', this._onPointerMove);
    document.addEventListener('pointerup',     this._onPointerUp);
    document.addEventListener('pointercancel', this._onPointerUp);
  };

  _onPointerMove = (e) => {
    if (!this._dragging || e.pointerId !== this._activePointer) return;

    const dx = e.clientX - this._dragStartPointerX;
    const dy = e.clientY - this._dragStartPointerY;
    const dist = Math.hypot(dx, dy);
    if (dist > this._dragMaxDist) this._dragMaxDist = dist;

    // Update position with mild rubber-band on overscroll for tactile feel
    let next = this._dragStartX + dx;
    if (next > this.maxX)      next = this.maxX + (next - this.maxX) * 0.4;
    else if (next < this.minX) next = this.minX + (next - this.minX) * 0.4;
    this.x = next;

    // Track instantaneous velocity (px / 16ms)
    const dt = Math.max(1, e.timeStamp - this._lastPointerT);
    const vx = ((e.clientX - this._lastPointerX) / dt) * 16;
    this._velocityHistory.push(vx);
    if (this._velocityHistory.length > VELOCITY_HISTORY) this._velocityHistory.shift();
    this._lastPointerX = e.clientX;
    this._lastPointerT = e.timeStamp;

    // Drag-velocity tilt
    const recentV = avg(this._velocityHistory);
    const tilt = clamp(recentV * TILT_PER_VELOCITY, -TILT_MAX_DEG, TILT_MAX_DEG);
    this.cards.forEach((c) => c.setDragRotation(tilt));
  };

  _onPointerUp = (e) => {
    if (!this._dragging || (e && e.pointerId !== this._activePointer)) return;

    this._dragging = false;
    this._activePointer = null;
    this.root.classList.remove('is-dragging');

    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('pointerup',     this._onPointerUp);
    document.removeEventListener('pointercancel', this._onPointerUp);

    // Project momentum from average release velocity
    const v = avg(this._velocityHistory);
    const projected = this.x + v * MOMENTUM_AMPLIFY;
    const target = clamp(projected, this.minX, this.maxX);

    this._momentumTween = gsap.to(this, {
      x: target,
      duration: MOMENTUM_DURATION,
      ease: 'power3.out',
    });

    // Ease tilt back to zero
    this.cards.forEach((c) => c.releaseDragRotation());
  };

  // Synthetic click suppression when the gesture exceeded the click threshold.
  _onClick = (e) => {
    if (this._dragMaxDist > CLICK_THRESHOLD_PX) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const cardEl = e.target.closest('.card');
    if (!cardEl || !this.root.contains(cardEl)) return;
    const world = cardEl.dataset.world;
    this.onCardClick(world, cardEl);
  };

  // ── wheel (horizontal trackpad / shift+wheel) ───────────────

  _onWheel = (e) => {
    if (isStacked()) return;
    const ax = Math.abs(e.deltaX);
    const ay = Math.abs(e.deltaY);
    // Only intercept clearly-horizontal wheel; otherwise let Lenis scroll the page.
    if (ax < ay * WHEEL_HORIZONTAL_DOMINANCE) return;
    e.preventDefault();
    this._momentumTween?.kill();
    this.x = clamp(this.x - e.deltaX, this.minX, this.maxX);
  };

  // ── render tick ─────────────────────────────────────────────

  _settled() {
    return this.cards.every((c) =>
      c.opacity > 0.99 &&
      Math.abs(c.enterY) < 0.4 &&
      Math.abs(c.idleY) < 0.4 &&
      Math.abs(c.hoverY) < 0.4 &&
      Math.abs(c.dragRot) < 0.4
    );
  }

  _tick = () => {
    this._raf = requestAnimationFrame(this._tick);

    // Composite carousel translate (none when stacked on phones)
    if (!isStacked()) {
      const tx = this.x.toFixed(2);
      if (tx !== this._tx) {
        this._tx = tx;
        this.root.style.transform = `translate3d(${tx}px, 0, 0)`;
      }
    }

    const target = this._scrollTarget;
    const cheap = lagOff();
    let dirty = false;

    // Composite each card's local transform from its state properties.
    for (let i = 0; i < this.cards.length; i++) {
      const c = this.cards[i];

      if (!cheap) {
        // Per-card scroll-lag chase. follow lerps toward the live page-scroll
        // target with this card's individual rate. The visible offset is the
        // gap (target − follow): when scrolling down, follow trails so the
        // gap is positive and the card sits slightly lower than its layout
        // position, reading as "lag". When scroll stops, follow catches up
        // and the gap closes to zero.
        const lerp = SCROLL_LAG_LERPS[i] ?? 0.22;
        this._cardScrollFollow[i] += (target - this._cardScrollFollow[i]) * lerp;
        c.scrollY = clamp(target - this._cardScrollFollow[i], -SCROLL_LAG_MAX_PX, SCROLL_LAG_MAX_PX);
      } else if (c.scrollY) {
        c.scrollY = 0;
      }

      const totalY = c.idleY + c.hoverY + c.scrollY + c.enterY;
      if (cheap) {
        const sig = `${totalY.toFixed(2)}|${c.opacity.toFixed(3)}|${c.dragRot.toFixed(2)}`;
        if (sig === c._paint) continue;
        c._paint = sig;
        dirty = true;
      }
      c.el.style.opacity = c.opacity;
      c.el.style.transform =
        `translate3d(0, ${totalY.toFixed(2)}px, 0) rotate(${c.dragRot.toFixed(2)}deg)`;
    }

    // Once the stacked entrance has landed, stop writing transforms every
    // frame. Page scroll then moves the cards with the document.
    if (isStacked() && !dirty && this._settled()) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
  };

  // ── teardown ────────────────────────────────────────────────

  destroy() {
    cancelAnimationFrame(this._raf);
    this._momentumTween?.kill();
    this._lenisOff?.();
    if (this._onNativeScroll) window.removeEventListener('scroll', this._onNativeScroll);
    this._entranceObserver?.disconnect();
    this._sectionIo?.disconnect();
    window.removeEventListener('resize', this._onResize);

    this.root.removeEventListener('pointerdown', this._onPointerDown);
    this.root.removeEventListener('click', this._onClick);
    this.section.removeEventListener('wheel', this._onWheel);
    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('pointerup',     this._onPointerUp);
    document.removeEventListener('pointercancel', this._onPointerUp);

    this.cards.forEach((c) => c.destroy());
    this.cardVideos?.destroy();
    document.removeEventListener('randomize', this._onRandomize);
  }
}

// ── helpers ──────────────────────────────────────────────────

function clamp(v, min, max) {
  return v < min ? min : v > max ? max : v;
}

function avg(arr) {
  if (!arr.length) return 0;
  let sum = 0;
  for (let i = 0; i < arr.length; i++) sum += arr[i];
  return sum / arr.length;
}
